import React, { useEffect, useState } from "react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import { GitCompare, Star } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { TRAITS, scoreColor, scoreLabel } from "@/lib/traits";

function ScorePill({ label, score, color }) {
  return (
    <div className="text-center">
      <div className="font-mono text-xs uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-display text-5xl" style={{ fontWeight: 900, color }}>{score > 0 ? score.toFixed(1) : "—"}</div>
      <div className="text-xs" style={{ color: scoreColor(score) }}>{scoreLabel(score)}</div>
    </div>
  );
}

export default function Compare() {
  const [companies, setCompanies] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (r.data[0]) setA(r.data[0].name);
      if (r.data[1]) setB(r.data[1].name);
    });
  }, []);

  useEffect(() => { if (a) api.get(`/companies/${encodeURIComponent(a)}`).then((r) => setDataA(r.data)); }, [a]);
  useEffect(() => { if (b) api.get(`/companies/${encodeURIComponent(b)}`).then((r) => setDataB(r.data)); }, [b]);

  const radarData = TRAITS.map((t) => ({
    trait: t.short,
    [a || "A"]: dataA?.traits?.[t.key] || 0,
    [b || "B"]: dataB?.traits?.[t.key] || 0,
  }));

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2">Head to head</p>
      <h1 className="font-display text-5xl text-slate-900 mb-2 flex items-center gap-3" style={{ fontWeight: 900 }}>
        <GitCompare className="text-emerald-600" /> Company Comparison
      </h1>
      <p className="text-slate-500 mb-8">Compare two organizations' leadership scores side by side.</p>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <Select value={a} onValueChange={setA}>
          <SelectTrigger data-testid="compare-select-a" className="h-12"><SelectValue placeholder="Select company A" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={b} onValueChange={setB}>
          <SelectTrigger data-testid="compare-select-b" className="h-12"><SelectValue placeholder="Select company B" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {dataA && dataB && (
        <div data-testid="compare-result">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 grid grid-cols-3 items-center mb-6">
            <ScorePill label={dataA.name} score={dataA.org_score} color="#059669" />
            <div className="text-center font-display text-3xl text-slate-300" style={{ fontWeight: 800 }}>VS</div>
            <ScorePill label={dataB.name} score={dataB.org_score} color="#6366f1" />
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-4">Trait comparison</p>
            <div style={{ height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="trait" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <PolarRadiusAxis domain={[0, 5]} tick={false} axisLine={false} />
                  <Radar dataKey={a} stroke="#059669" fill="#059669" fillOpacity={0.3} />
                  <Radar dataKey={b} stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-4">Trait breakdown</p>
            <div className="space-y-3">
              {TRAITS.map((t) => {
                const va = dataA.traits?.[t.key] || 0;
                const vb = dataB.traits?.[t.key] || 0;
                return (
                  <div key={t.key} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-sm">
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-mono font-semibold" style={{ color: va >= vb ? "#059669" : "#94a3b8" }}>{va.toFixed(1)}</span>
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 ml-auto" style={{ width: `${(va / 5) * 100}%` }} /></div>
                    </div>
                    <span className="text-slate-500 text-xs text-center w-28 flex items-center justify-center gap-1"><t.icon size={12} /> {t.short}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-500" style={{ width: `${(vb / 5) * 100}%` }} /></div>
                      <span className="font-mono font-semibold" style={{ color: vb >= va ? "#6366f1" : "#94a3b8" }}>{vb.toFixed(1)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
